import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import urls from "../../global/constants/UrlConstants";
import YhButton from "../../ui/YhButton";
import YhInput from "../../ui/YhInput";
import YhModal from "../../ui/YhModal";
import YhSelect, { buildStageSelectGroups } from "../../ui/YhSelect";
import YhMultiSelect from "../../ui/YhMultiSelect";
import YhTable, { YhTableColumn } from "../../ui/YhTable";
import {
  YhDatePicker,
  YhDateTimePicker,
  formatDisplayDateTime,
  formatRecordedAt,
  parseLocalDateTime,
} from "../../ui/YhDateTimePicker";
import {
  changeStage,
  getAllLeadHistory,
  getAllSource,
  getAllTeamData,
  getAllTeamMembersData,
  getLeadById,
  getReminder,
  saveReminder,
} from "./components/ShowAll/ShowAllService";
import {
  getRemark,
  saveRemark,
} from "../MyLead/components/ShowAll/ShowAllService";
import {
  addTask,
  getTask,
} from "./components/stepper copy/Personal Details/PersonalDetailsService";
import { updatePersonalDetail } from "./components/stepper/StepperService";
import { getStage } from "../MetaData/components/MetaList/components/LeadStageData/LeadStageListService";
import { clickToCall } from "../Team/components/ShowAll/ShowAllService";
import { useActiveIvrCalls, refreshActiveIvrCalls, seedActiveIvrCall } from "../../utils/useActiveIvrCalls";
import { store } from "../../utils/store";
import { setUuid } from "../../redux/authSlice";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../helpers/methods";
import {
  openSms,
  openWhatsApp,
  WHATSAPP_TEMPLATES,
} from "../../helpers/phoneActions";
import { getLeadListReturnPath } from "../../helpers/leadListFilters";
import AllLeadSideDrawer from "./components/AllLeadSideDrawer";
import { parseLeadExtraFields } from "../Customer/leadExtraFields";
import {
  formatPhoneKeepingCountryCode,
  resolveLeadPhoneForDisplay,
} from "../../Shared/Phone/PhoneWithCountryCode";
import EntityCallHistory from "../Shared/EntityCallHistory";
import {
  getDeveloper,
  getProject,
  getProjectByDeveloper,
  getPropetyType,
} from "../Project/components/stepper/StepperService";
import { useSelector } from "react-redux";
import { RootState } from "../../utils/store";
import {
  canEditLeadSource,
  isNiOrLostStage,
} from "../../helpers/salesLeadHelpers";
import StageReasonModal from "../../global/components/StageReasonModal";

const PRIORITY_OPTIONS = [
  { label: "Low", value: "Low" },
  { label: "Medium", value: "Medium" },
  { label: "High", value: "High" },
];

const emptyTaskForm = () => ({
  priority: "",
  assignTo: [] as Array<string | number>,
  assignToMember: [] as Array<string | number>,
  dueDate: "",
  closedDate: "",
  description: "",
});

const formatAssignIds = (val: any) => {
  if (!val || val === "NA") return "NA";
  if (typeof val === "string") return val;
  if (Array.isArray(val)) {
    const ids = val
      .map((item) => {
        if (item == null) return null;
        if (typeof item === "object") return item.id || item._id || item.userId;
        return item;
      })
      .filter(Boolean)
      .map((id) => `'${id}'`);
    return ids.length ? ids.join(",") : "NA";
  }
  return "NA";
};

type RouteParams = { id: string };
type TabKey = "activities" | "details" | "calls" | "task" | "reminder" | "notes";

const STORAGE_KEY = (id: string) => `all-lead-details-${id}`;

const TABS: { key: TabKey; label: string }[] = [
  { key: "activities", label: "Activities" },
  { key: "details", label: "Lead Details" },
  { key: "calls", label: "Calls" },
  { key: "task", label: "Task" },
  { key: "reminder", label: "Reminder" },
  { key: "notes", label: "Notes" },
];

const EmptyState = ({
  title,
  subtitle,
  actionLabel,
  onAction,
}: {
  title: string;
  subtitle: string;
  actionLabel?: string;
  onAction?: () => void;
}) => (
  <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
    <div className="mb-5 flex h-28 w-28 items-center justify-center rounded-3xl bg-gradient-to-br from-[#F9F0F4] to-[#F5F3FF]">
      <svg width="56" height="56" viewBox="0 0 24 24" fill="none" className="text-[#841C39]">
        <path
          d="M7 3h7l5 5v13a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path d="M14 3v5h5M8 13h8M8 17h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    </div>
    <p className="text-base font-semibold text-[#111827]">{title}</p>
    <p className="mt-1 max-w-sm text-sm text-[#6B7280]">{subtitle}</p>
    {actionLabel && onAction ? (
      <YhButton variant="primary" className="mt-5" onClick={onAction}>
        {actionLabel}
      </YhButton>
    ) : null}
  </div>
);

const DetailRow = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: React.ReactNode;
}) => (
  <div className="flex items-start gap-3 py-2.5">
    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#F3F4F6] text-[#841C39]">
      {icon}
    </span>
    <div className="min-w-0">
      <p className="text-[11px] font-medium uppercase tracking-wide text-[#9CA3AF]">
        {label}
      </p>
      <p className="truncate text-sm font-medium text-[#111827]">
        {value || "—"}
      </p>
    </div>
  </div>
);

function normalizeLeadRouteId(raw?: string) {
  const text = decodeURIComponent(String(raw || "").trim());
  const oid = text.match(/[a-fA-F0-9]{24}/);
  if (oid) return oid[0];
  return text.replace(/^.*\//, "").split("?")[0].trim();
}

const AllLeadsDetails = () => {
  const history = useHistory();
  const location = useLocation<any>();
  const { id: rawId } = useParams<RouteParams>();
  const id = normalizeLeadRouteId(rawId);

  const [lead, setLead] = useState<any>(null);
  const [tab, setTab] = useState<TabKey>(
    location.state?.type === "reminder"
      ? "reminder"
      : location.state?.type === "note"
        ? "notes"
        : "activities"
  );
  const [historyRows, setHistoryRows] = useState<any[]>([]);
  const [reminders, setReminders] = useState<any[]>([]);
  const [notes, setNotes] = useState<any[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [noteModal, setNoteModal] = useState(false);
  const [reminderModal, setReminderModal] = useState(false);
  const [taskModal, setTaskModal] = useState(false);
  const [savingTask, setSavingTask] = useState(false);
  const [taskForm, setTaskForm] = useState(emptyTaskForm);
  const [taskTeamOptions, setTaskTeamOptions] = useState<any[]>([]);
  const [taskMemberOptions, setTaskMemberOptions] = useState<
    { label: string; value: string | number }[]
  >([]);
  /** Mirrors side-drawer Assigned To / Member so Add Task shows the same chips */
  const [drawerAssign, setDrawerAssign] = useState<{
    teams: Array<{ id: string; label: string }>;
    members: Array<{ id: string; label: string }>;
    teamOptions: Array<{ id: string; label: string }>;
    memberOptions: Array<{ id: string; label: string }>;
  }>({
    teams: [],
    members: [],
    teamOptions: [],
    memberOptions: [],
  });
  const [noteText, setNoteText] = useState("");
  const [reminderForm, setReminderForm] = useState({
    title: "",
    dateTime: "",
    notes: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [stageData, setStageData] = useState<any[]>([]);
  const [editForm, setEditForm] = useState({
    leadName: "",
    leadEmail: "",
    leadPhone: "",
    developerId: "",
    projectId: "",
    projecttypeId: "",
  });
  const [developerOptions, setDeveloperOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [projectOptions, setProjectOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [projectTypeOptions, setProjectTypeOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const hierarchyLevel = useSelector(
    (state: RootState) => state.auth.hierarchyLevel
  );
  const auth = useSelector((state: RootState) => state.auth);
  const isOrgAdmin = String(hierarchyLevel) === "1";
  const allowEditSource = canEditLeadSource(auth);
  const [sourceOptions, setSourceOptions] = useState<string[]>([]);
  const [stageReason, setStageReason] = useState<{
    open: boolean;
    stage: string;
  }>({ open: false, stage: "" });
  const [savingStageReason, setSavingStageReason] = useState(false);
  const [customerConfirm, setCustomerConfirm] = useState<{
    open: boolean;
    stage: string;
  }>({ open: false, stage: "" });
  const [convertingCustomer, setConvertingCustomer] = useState(false);
  const [calling, setCalling] = useState(false);
  const { hasActiveCall, primary } = useActiveIvrCalls();
  const callBusy = calling || hasActiveCall;

  const stageGroups = useMemo(
    () => buildStageSelectGroups(stageData),
    [stageData]
  );

  const extraFields = useMemo(() => parseLeadExtraFields(lead), [lead]);
  const displayProjectName = useMemo(() => {
    const fromLead = String(lead?.project_name || "").trim();
    if (fromLead && fromLead !== "NA") return fromLead;
    const hit = (extraFields || []).find((f) =>
      /interested\s*project(?!\s*id)/i.test(String(f?.label || ""))
    );
    return String(hit?.value || "").trim();
  }, [lead?.project_name, extraFields]);

  const initialLead = useMemo(() => {
    if (location.state?.lead) return location.state.lead;
    try {
      const cached = sessionStorage.getItem(STORAGE_KEY(id));
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  }, [id, location.state]);

  useEffect(() => {
    let cancelled = false;

    const applyLead = (next: any) => {
      if (cancelled || !next) return;
      setLead(next);
      try {
        sessionStorage.setItem(STORAGE_KEY(id), JSON.stringify(next));
      } catch {
        // ignore storage failures
      }
    };

    const hasDetails = (row: any) =>
      !!(row?.leadName || row?.leadEmail || row?.leadPhone || row?.form_name);

    (async () => {
      // Show cached/nav state immediately, then always refresh from API so
      // project / developer / type names (and phone mask flags) are correct.
      if (initialLead?._id && hasDetails(initialLead)) {
        applyLead(initialLead);
      } else if (id) {
        setLead(initialLead?._id ? initialLead : { _id: id });
      }

      if (id) {
        try {
          const res = await getLeadById(id);
          const raw = res?.data;
          const fetched = Array.isArray(raw) ? raw[0] : raw;
          if (fetched && (fetched._id || fetched.id)) {
            applyLead({
              ...(initialLead || {}),
              ...fetched,
              _id: fetched._id || fetched.id || id,
            });
          }
        } catch {
          // keep stub so history/tabs can still load with id
        }
      }

      setDrawerAssign({
        teams: [],
        members: [],
        teamOptions: [],
        memberOptions: [],
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [id, initialLead]);

  const loadTabData = async (leadId: string) => {
    setLoading(true);
    try {
      const [hist, rem, remark, taskRes] = await Promise.all([
        getAllLeadHistory(leadId).catch(() => ({ data: [] })),
        getReminder(leadId).catch(() => ({ data: [] })),
        getRemark(leadId).catch(() => ({ data: [] })),
        getTask(leadId).catch(() => ({ data: [] })),
      ]);
      setHistoryRows(Array.isArray(hist?.data) ? hist.data : []);
      setReminders(Array.isArray(rem?.data) ? rem.data : []);
      setNotes(Array.isArray(remark?.data) ? remark.data : []);
      setTasks(Array.isArray(taskRes?.data) ? taskRes.data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (lead?._id) loadTabData(lead._id);
  }, [lead?._id]);

  useEffect(() => {
    getStage()
      .then((res) => setStageData(res?.data || []))
      .catch(() => setStageData([]));
    if (allowEditSource) {
      getAllSource()
        .then((res) => {
          const seen = new Set<string>();
          const names: string[] = [];
          for (const e of res?.data || []) {
            const name = String(e?.source_name || "").trim();
            if (!name) continue;
            const key = name.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            names.push(name);
          }
          setSourceOptions(names);
        })
        .catch(() => setSourceOptions([]));
    }
  }, [allowEditSource]);

  const applyStageChange = async (nextStage: string, reason?: string) => {
    if (!lead?._id || !nextStage || nextStage === lead.stage) return;
    try {
      const body: any = { id: lead._id, stage: nextStage };
      if (reason) {
        body.reason = reason;
        body.stageReason = reason;
      }
      const res = await changeStage(body);
      if (res?.success === 1) {
        const updated = { ...lead, stage: nextStage };
        setLead(updated);
        sessionStorage.setItem(STORAGE_KEY(id), JSON.stringify(updated));
        const converted =
          res?.customerConverted ||
          String(nextStage).trim().toLowerCase() === "customer";
        openSuccessNotification(
          converted
            ? res?.message || "Lead converted to customer successfully"
            : res?.message || "Stage updated"
        );
        getAllLeadHistory(lead._id)
          .then((hist) =>
            setHistoryRows(Array.isArray(hist?.data) ? hist.data : [])
          )
          .catch(() => undefined);
        if (converted) {
          history.push(urls.customerListViewPath);
        }
      } else {
        openErrorNotification(res?.message || "Failed to update stage");
      }
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to update stage");
    }
  };

  const handleStageChange = async (nextStage: string) => {
    if (!lead?._id || !nextStage || nextStage === lead.stage) return;
    if (String(nextStage).trim().toLowerCase() === "customer") {
      setCustomerConfirm({ open: true, stage: nextStage });
      return;
    }
    if (isNiOrLostStage(nextStage)) {
      setStageReason({ open: true, stage: nextStage });
      return;
    }
    await applyStageChange(nextStage);
  };

  const confirmStageReason = async (reason: string) => {
    if (!lead?._id || !stageReason.stage) return;
    try {
      setSavingStageReason(true);
      // Pass reason on stage update — backend requires it for NI/Lost and
      // also writes the remark; avoid a separate save that then fails the PUT.
      await applyStageChange(stageReason.stage, reason);
      setStageReason({ open: false, stage: "" });
      getRemark(lead._id)
        .then((remark) =>
          setNotes(Array.isArray(remark?.data) ? remark.data : [])
        )
        .catch(() => undefined);
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to save reason");
    } finally {
      setSavingStageReason(false);
    }
  };

  const updateLeadSource = async (nextSource: string) => {
    if (!lead?._id || !nextSource || nextSource === lead.source) return;
    if (!allowEditSource) {
      openErrorNotification("Only Admin can edit lead source");
      return;
    }
    try {
      const body = {
        id: lead._id,
        formId: lead.formId,
        form_name: lead.form_name,
        developerId: lead.developerId || "NA",
        projectId: lead.projectId || "NA",
        projecttypeId: lead.projecttypeId || "NA",
        leadName: lead.leadName,
        leadEmail: lead.leadEmail,
        leadPhone: lead.leadPhone,
        dynamicFields: lead.dynamicFields || [],
        AssignTo: lead.AssignTo,
        AssignToUser: lead.AssignToUser,
        stage: lead.stage || "NA",
        status: lead.status,
        source: nextSource,
      };
      const res = await updatePersonalDetail(body);
      if (res?.success) {
        const updated = { ...lead, source: nextSource };
        setLead(updated);
        openSuccessNotification(res?.message || "Source updated");
      } else {
        openErrorNotification(res?.message || "Failed to update source");
      }
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to update source");
    }
  };

  const confirmConvertCustomer = async () => {
    if (!customerConfirm.stage) return;
    try {
      setConvertingCustomer(true);
      await applyStageChange(customerConfirm.stage);
      setCustomerConfirm({ open: false, stage: "" });
    } finally {
      setConvertingCustomer(false);
    }
  };

  const isHometatvaLead =
    location.pathname.includes("hometatva") ||
    location.state?.from === "hometatva";

  const handleBack = () => {
    if (isHometatvaLead) {
      history.push(urls.HOMETATVALEADFORMLIST);
      return;
    }
    const fromState =
      typeof location.state?.from === "string" ? location.state.from : "";
    const fallback = urls.ALLLEADFORMLIST;
    history.push(fromState || getLeadListReturnPath(fallback));
  };

  const startEdit = async () => {
    if (!lead) return;
    const ensureOption = (
      options: { label: string; value: string }[],
      value?: string,
      label?: string
    ) => {
      const v = value && value !== "NA" ? String(value) : "";
      if (!v) return options;
      if (options.some((o) => o.value === v)) return options;
      return [{ label: label || v, value: v }, ...options];
    };

    setEditForm({
      leadName: lead.leadName || "",
      leadEmail: lead.leadEmail || "",
      leadPhone: resolveLeadPhoneForDisplay(lead) || lead.leadPhone || "",
      developerId: lead.developerId && lead.developerId !== "NA" ? String(lead.developerId) : "",
      projectId: lead.projectId && lead.projectId !== "NA" ? String(lead.projectId) : "",
      projecttypeId:
        lead.projecttypeId && lead.projecttypeId !== "NA"
          ? String(lead.projecttypeId)
          : "",
    });
    setIsEditing(true);
    setTab("details");

    try {
      const [devRes, typeRes] = await Promise.all([
        getDeveloper().catch(() => null),
        getPropetyType().catch(() => null),
      ]);
      const devRows = Array.isArray(devRes?.data)
        ? devRes.data
        : Array.isArray(devRes)
          ? devRes
          : [];
      setDeveloperOptions(
        ensureOption(
          devRows
            .map((d: any) => ({
              label: d.developer_name || d.name || "—",
              value: String(d._id || d.id || ""),
            }))
            .filter((o: any) => o.value),
          lead.developerId,
          lead.developer_name
        )
      );
      const typeRows = Array.isArray(typeRes?.data)
        ? typeRes.data
        : Array.isArray(typeRes)
          ? typeRes
          : [];
      setProjectTypeOptions(
        ensureOption(
          typeRows
            .map((t: any) => ({
              label: t.name || t.property_type || t.projecttype_name || "—",
              value: String(t._id || t.id || ""),
            }))
            .filter((o: any) => o.value),
          lead.projecttypeId,
          lead.projecttype_name
        )
      );

      const developerId =
        lead.developerId && lead.developerId !== "NA"
          ? String(lead.developerId)
          : "";
      const projRes = developerId
        ? await getProjectByDeveloper(developerId).catch(() => null)
        : await getProject().catch(() => null);
      const projRows = Array.isArray(projRes?.data)
        ? projRes.data
        : Array.isArray(projRes)
          ? projRes
          : [];
      setProjectOptions(
        ensureOption(
          projRows
            .map((p: any) => ({
              label: p.project_name || p.name || "—",
              value: String(p._id || p.id || ""),
            }))
            .filter((o: any) => o.value),
          lead.projectId,
          lead.project_name
        )
      );
    } catch {
      /* dropdowns stay empty — still seed current values */
      setDeveloperOptions(
        ensureOption([], lead.developerId, lead.developer_name)
      );
      setProjectOptions(ensureOption([], lead.projectId, lead.project_name));
      setProjectTypeOptions(
        ensureOption([], lead.projecttypeId, lead.projecttype_name)
      );
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditForm({
      leadName: lead?.leadName || "",
      leadEmail: lead?.leadEmail || "",
      leadPhone: resolveLeadPhoneForDisplay(lead) || lead?.leadPhone || "",
      developerId:
        lead?.developerId && lead?.developerId !== "NA"
          ? String(lead.developerId)
          : "",
      projectId:
        lead?.projectId && lead?.projectId !== "NA" ? String(lead.projectId) : "",
      projecttypeId:
        lead?.projecttypeId && lead?.projecttypeId !== "NA"
          ? String(lead.projecttypeId)
          : "",
    });
  };

  const handleSaveLead = async () => {
    if (!lead?._id && !lead?.id) return;
    const leadId = lead._id || lead.id;
    if (!editForm.leadName.trim()) {
      openErrorNotification("Full name is required");
      return;
    }
    setSaving(true);
    try {
      const body: any = {
        id: leadId,
        formId: lead.formId,
        form_name: lead.form_name,
        developerId: editForm.developerId || lead.developerId || "NA",
        projectId: editForm.projectId || lead.projectId || "NA",
        projecttypeId: editForm.projecttypeId || lead.projecttypeId || "NA",
        leadName: editForm.leadName.trim(),
        leadEmail: editForm.leadEmail.trim(),
        dynamicFields: lead.dynamicFields || [],
        AssignTo: formatAssignIds(lead.AssignTo),
        AssignToUser: formatAssignIds(lead.AssignToUser),
        stage: lead.stage || "NA",
        status: lead.status,
        source: lead.source,
      };
      // Only org admin may send a phone update (team phone stays as-is server-side too)
      if (isOrgAdmin) {
        const phone = editForm.leadPhone.trim();
        if (phone && !phone.includes("*")) {
          body.leadPhone = phone;
        } else {
          body.leadPhone = lead.leadPhoneFull || lead.leadPhone;
        }
      } else {
        body.leadPhone = lead.leadPhoneFull || lead.leadPhone;
      }
      const res = await updatePersonalDetail(body);
      if (res?.success) {
        const devLabel =
          developerOptions.find((o) => o.value === editForm.developerId)
            ?.label || lead.developer_name;
        const projLabel =
          projectOptions.find((o) => o.value === editForm.projectId)?.label ||
          lead.project_name;
        const typeLabel =
          projectTypeOptions.find((o) => o.value === editForm.projecttypeId)
            ?.label || lead.projecttype_name;
        const updated = {
          ...lead,
          leadName: editForm.leadName.trim(),
          leadEmail: editForm.leadEmail.trim(),
          ...(isOrgAdmin && !editForm.leadPhone.includes("*")
            ? { leadPhone: editForm.leadPhone.trim() }
            : {}),
          developerId: editForm.developerId || lead.developerId,
          projectId: editForm.projectId || lead.projectId,
          projecttypeId: editForm.projecttypeId || lead.projecttypeId,
          developer_name: devLabel,
          project_name: projLabel,
          projecttype_name: typeLabel,
        };
        setLead(updated);
        sessionStorage.setItem(STORAGE_KEY(id), JSON.stringify(updated));
        setIsEditing(false);
        openSuccessNotification(
          res?.message || "Lead Details Updated Successfully"
        );
        // Refresh from API for masked flags + canonical names
        try {
          const fresh = await getLeadById(String(leadId));
          const raw = fresh?.data;
          const fetched = Array.isArray(raw) ? raw[0] : raw;
          if (fetched) {
            setLead({ ...updated, ...fetched, _id: fetched._id || leadId });
          }
        } catch {
          /* keep local updated */
        }
      } else {
        openErrorNotification(res?.message || "Failed to update lead details");
      }
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to update lead details");
    } finally {
      setSaving(false);
    }
  };

  const initials = useMemo(() => {
    const name = String(
      (isEditing ? editForm.leadName : lead?.leadName) || "?"
    ).trim();
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase() || "?";
  }, [lead?.leadName, isEditing, editForm.leadName]);

  const normalizePhoneNumber = (phone: string) => {
    if (!phone) return "";
    const raw = String(phone);
    if (raw.includes("*")) return "";
    const digits = raw.replace(/\D/g, "");
    if (!digits) return "";
    if (digits.length === 10) return `+91${digits}`;
    if (digits.startsWith("91") && digits.length === 12) return `+${digits}`;
    return `+${digits}`;
  };

  const getMessagingPhone = () =>
    normalizePhoneNumber(lead?.leadPhoneFull || lead?.leadPhone || "");

  const handleWhatsApp = (templateId?: string) => {
    const phone = getMessagingPhone();
    if (!phone) {
      openErrorNotification("Valid phone number required for WhatsApp");
      return;
    }
    const tpl = WHATSAPP_TEMPLATES.find((t) => t.id === templateId);
    openWhatsApp(phone, tpl ? tpl.build(lead?.leadName || "") : undefined);
  };

  const handleSms = () => {
    const phone = getMessagingPhone();
    if (!phone) {
      openErrorNotification("Valid phone number required for SMS");
      return;
    }
    openSms(phone);
  };

  const handleClickToCall = async () => {
    try {
      if (hasActiveCall) {
        openErrorNotification(
          `Call already in progress (${primary?.durationFormatted || "live"}). Wait until it ends.`
        );
        return;
      }
      const dialable = normalizePhoneNumber(
        lead?.leadPhoneFull || lead?.leadPhone || ""
      );
      const uuid = store.getState().auth.uuid;
      if (!uuid) {
        openErrorNotification("Caller ID (uuid) missing for this user");
        return;
      }
      if (!dialable && !lead?._id) {
        openErrorNotification("Valid phone number required to call");
        return;
      }
      setCalling(true);
      const auth = store.getState().auth;
      const response = await clickToCall({
        leadPhone: dialable || lead?.leadPhone,
        leadId: lead?._id,
        leadName: lead?.leadName || lead?.name || "",
        agentName: auth.userName || "",
        uuid,
      });
      if (
        response?.success === 0 ||
        response?.success === false ||
        response?.error
      ) {
        openErrorNotification(
          response?.message || response?.error || "Unable to place call"
        );
        return;
      }
      if (response?.uuid && String(response.uuid) !== String(uuid)) {
        store.dispatch(setUuid(String(response.uuid)));
      }
      openSuccessNotification(response?.message || "Call initiated");
      seedActiveIvrCall({
        _id: response?.activeCallId || response?.data?.activeCallId,
        status: "initiated",
        leadName: lead?.leadName || lead?.name || "",
        leadId: String(lead?._id || ""),
        customerNumber: dialable || lead?.leadPhone,
        agentUserId: response?.uuid || uuid,
        uniqueCallId: response?.unique_id || response?.data?.unique_id,
        referenceId: response?.reference_id || response?.referenceId,
      });
      await refreshActiveIvrCalls();
    } catch (e: any) {
      openErrorNotification(e?.message || "Unable to place call");
    } finally {
      setCalling(false);
    }
  };

  const handleSaveNote = async () => {
    if (!noteText.trim() || !lead?._id) return;
    try {
      const payload = {
        leadId: lead._id,
        notes: noteText.trim(),
        date: new Date().toISOString().split("T")[0],
        time: new Date().toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        }),
        title: "",
      };
      const res = await saveRemark(payload);
      if (res?.success === 1 || res?.data) {
        openSuccessNotification(res?.message || "Note added");
        setNoteModal(false);
        setNoteText("");
        const remark = await getRemark(lead._id);
        setNotes(Array.isArray(remark?.data) ? remark.data : []);
      }
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to save note");
    }
  };

  const handleSaveReminder = async () => {
    if (!reminderForm.title || !reminderForm.dateTime || !lead?._id) return;
    try {
      const [date, time] = reminderForm.dateTime.split("T");
      const body = {
        leadId: lead._id,
        title: reminderForm.title,
        notes: reminderForm.notes,
        date,
        time,
      };
      const res = await saveReminder(body);
      if (res?.success === 1 || res?.data) {
        openSuccessNotification(res?.message || "Reminder created");
        setReminderModal(false);
        setReminderForm({ title: "", dateTime: "", notes: "" });
        const rem = await getReminder(lead._id);
        setReminders(Array.isArray(rem?.data) ? rem.data : []);
      }
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to save reminder");
    }
  };

  const cleanId = (val: any) =>
    String(val ?? "")
      .replace(/['"]/g, "")
      .trim();

  const parseLeadTeamIds = (raw: any): string[] => {
    if (!raw || raw === "NA") return [];
    if (Array.isArray(raw)) {
      return raw
        .map((item: any) => {
          if (item == null) return "";
          if (typeof item === "string" || typeof item === "number") {
            return cleanId(item);
          }
          return cleanId(item.team_id || item._id || item.id);
        })
        .filter(Boolean);
    }
    if (typeof raw === "string") {
      return raw
        .split(",")
        .map((s) => cleanId(s))
        .filter((s) => s && s !== "NA");
    }
    return [];
  };

  const parseLeadMemberIds = (raw: any): string[] => {
    if (!raw || raw === "NA") return [];
    if (Array.isArray(raw)) {
      return raw
        .map((item: any) => {
          if (item == null) return "";
          if (typeof item === "string" || typeof item === "number") {
            return cleanId(item);
          }
          return cleanId(item.user_id || item.userId || item._id || item.id);
        })
        .filter(Boolean);
    }
    if (typeof raw === "string") {
      return raw
        .split(",")
        .map((s) => cleanId(s))
        .filter((s) => s && s !== "NA");
    }
    return [];
  };

  const isFlagOn = (val: any) =>
    val === 1 || val === "1" || val === true;

  const closeTaskModal = () => {
    setTaskModal(false);
    setTaskForm(emptyTaskForm());
    setTaskMemberOptions([]);
  };

  const loadTaskMembersForTeams = async (
    teamIds: Array<string | number>,
    preferredMemberIds: Set<string>,
    teamOptionsFallback: any[] = []
  ): Promise<string[]> => {
    if (!teamIds.length || !lead?._id) {
      setTaskMemberOptions([]);
      return [];
    }

    const buildPreferredOptions = () => {
      const options: { label: string; value: string }[] = [];
      preferredMemberIds.forEach((uid) => {
        const fromLead = Array.isArray(lead?.AssignToUser)
          ? lead.AssignToUser.find(
              (m: any) =>
                cleanId(m?.user_id || m?.userId || m?._id || m?.id) === uid
            )
          : null;
        options.push({
          label: fromLead?.name || fromLead?.fullName || uid,
          value: uid,
        });
      });
      return options;
    };

    const pickPreferred = (options: { label: string; value: string }[]) =>
      Array.from(preferredMemberIds).filter((uid) =>
        options.some((o) => String(o.value) === uid)
      );

    try {
      const membersRes = await getAllTeamMembersData({
        team_id: teamIds,
        lead_id: lead._id,
      });
      const options = Array.from(
        new Map(
          (membersRes?.data || [])
            .flatMap((team: any) =>
              (team?.team_members || [])
                .map((mem: any) => {
                  const user = mem?.users?.[0];
                  if (!user) return null;
                  const id = cleanId(user._id || mem.userId);
                  if (!id) return null;
                  return {
                    label: user.name || id,
                    value: id,
                  };
                })
                .filter(Boolean)
            )
            .map((m: any) => [String(m.value), m])
        ).values()
      ) as { label: string; value: string }[];

      preferredMemberIds.forEach((uid) => {
        if (!options.some((o) => String(o.value) === uid)) {
          const fromLead = Array.isArray(lead?.AssignToUser)
            ? lead.AssignToUser.find(
                (m: any) =>
                  cleanId(m?.user_id || m?.userId || m?._id || m?.id) === uid
              )
            : null;
          options.push({
            label: fromLead?.name || fromLead?.fullName || uid,
            value: uid,
          });
        }
      });

      setTaskMemberOptions(options);
      return pickPreferred(options);
    } catch {
      const source =
        teamOptionsFallback.length > 0 ? teamOptionsFallback : taskTeamOptions;
      const selected = source.filter((t) =>
        teamIds.map(String).includes(String(t.id))
      );
      const options = Array.from(
        new Map(
          selected
            .flatMap((t) => t.teamMembers || [])
            .filter((m: any) => m?.id)
            .map((m: any) => [
              String(m.id),
              { label: m.label, value: cleanId(m.id) },
            ])
        ).values()
      ) as { label: string; value: string }[];

      if (!options.length) {
        const preferredOnly = buildPreferredOptions();
        setTaskMemberOptions(preferredOnly);
        return preferredOnly.map((o) => o.value);
      }

      preferredMemberIds.forEach((uid) => {
        if (!options.some((o) => String(o.value) === uid)) {
          options.push(
            ...buildPreferredOptions().filter((o) => o.value === uid)
          );
        }
      });

      setTaskMemberOptions(options);
      return pickPreferred(options);
    }
  };

  const openTaskModal = async () => {
    setTaskModal(true);

    // Prefer exact same selection as the side drawer (Team Deepak, Team One, +4, Sahil…)
    if (drawerAssign.teams.length > 0 || drawerAssign.members.length > 0) {
      const teamOpts =
        drawerAssign.teamOptions.length > 0
          ? drawerAssign.teamOptions.map((t) => ({
              id: t.id,
              label: t.label,
              teamMembers: [],
            }))
          : drawerAssign.teams.map((t) => ({
              id: t.id,
              label: t.label,
              teamMembers: [],
            }));
      const memberOpts =
        drawerAssign.memberOptions.length > 0
          ? drawerAssign.memberOptions.map((m) => ({
              label: m.label,
              value: m.id,
            }))
          : drawerAssign.members.map((m) => ({
              label: m.label,
              value: m.id,
            }));

      // Ensure selected members appear in options
      drawerAssign.members.forEach((m) => {
        if (!memberOpts.some((o) => String(o.value) === String(m.id))) {
          memberOpts.push({ label: m.label, value: m.id });
        }
      });

      setTaskTeamOptions(teamOpts);
      setTaskMemberOptions(memberOpts);
      setTaskForm({
        ...emptyTaskForm(),
        assignTo: drawerAssign.teams.map((t) => t.id),
        assignToMember: drawerAssign.members.map((m) => m.id),
      });
      return;
    }

    // Fallback if drawer has not loaded yet
    setTaskForm(emptyTaskForm());
    setTaskMemberOptions([]);
    if (!lead?._id) return;

    const assignedTeamIds = new Set(parseLeadTeamIds(lead.AssignTo));
    const assignedMemberIds = new Set(parseLeadMemberIds(lead.AssignToUser));

    try {
      const response1 = await getAllTeamData(lead._id);
      const assignToData = (response1?.data || []).flatMap((team: any) => {
        const subTeams =
          team.sub_teams?.map((subTeam: any) => {
            const tid = cleanId(subTeam._id);
            const selected =
              isFlagOn(subTeam?.is_available) || assignedTeamIds.has(tid);
            return {
              id: String(subTeam._id),
              label: subTeam.team_name,
              selected: selected ? 1 : 0,
              teamMembers: subTeam.team_members?.length
                ? subTeam.team_members.map((member: any) => ({
                    id: member.userId,
                    label: member.users?.[0]?.name,
                  }))
                : [],
            };
          }) || [];

        const tid = cleanId(team._id);
        const mainSelected =
          isFlagOn(team?.is_available) || assignedTeamIds.has(tid);
        return [
          {
            id: String(team._id),
            label: team.team_name,
            selected: mainSelected ? 1 : 0,
            teamMembers: team.team_members?.length
              ? team.team_members.map((member: any) => ({
                  id: member.userId,
                  label: member.users?.[0]?.name,
                }))
              : [],
          },
          ...subTeams,
        ];
      });

      if (Array.isArray(lead.AssignTo)) {
        lead.AssignTo.forEach((t: any) => {
          const tid = cleanId(t?.team_id || t?._id || t?.id);
          if (!tid) return;
          const idx = assignToData.findIndex((e: any) => cleanId(e.id) === tid);
          if (idx >= 0) assignToData[idx].selected = 1;
          else {
            assignToData.push({
              id: tid,
              label: t?.team_name || t?.label || tid,
              selected: 1,
              teamMembers: [],
            });
          }
        });
      } else {
        assignedTeamIds.forEach((tid) => {
          if (!tid) return;
          const idx = assignToData.findIndex((e: any) => cleanId(e.id) === tid);
          if (idx >= 0) assignToData[idx].selected = 1;
          else {
            assignToData.push({
              id: tid,
              label: tid,
              selected: 1,
              teamMembers: [],
            });
          }
        });
      }

      const allTeams = Array.from(
        new Map(assignToData.map((t: any) => [String(t.id), t])).values()
      );
      setTaskTeamOptions(allTeams);

      const fromLeadMatch = allTeams
        .filter((t: any) => assignedTeamIds.has(cleanId(t.id)))
        .map((t: any) => String(t.id));
      const fromAvailable = allTeams
        .filter((t: any) => t.selected === 1)
        .map((t: any) => String(t.id));
      const finalTeamIds =
        fromLeadMatch.length > 0
          ? fromLeadMatch
          : fromAvailable.length > 0
            ? fromAvailable
            : Array.from(assignedTeamIds);

      const memberIds = finalTeamIds.length
        ? await loadTaskMembersForTeams(
            finalTeamIds,
            assignedMemberIds,
            allTeams
          )
        : Array.from(assignedMemberIds);

      setTaskForm({
        ...emptyTaskForm(),
        assignTo: finalTeamIds,
        assignToMember: memberIds,
      });
    } catch {
      const teamOpts = Array.isArray(lead.AssignTo)
        ? lead.AssignTo.map((t: any) => ({
            id: cleanId(t?.team_id || t?._id || t?.id),
            label: t?.team_name || t?.label || "Team",
            selected: 1,
            teamMembers: [],
          })).filter((t: any) => t.id)
        : Array.from(assignedTeamIds).map((tid) => ({
            id: tid,
            label: tid,
            selected: 1,
            teamMembers: [],
          }));
      setTaskTeamOptions(teamOpts);

      const memberOpts = Array.isArray(lead.AssignToUser)
        ? lead.AssignToUser.map((m: any) => ({
            label: m?.name || m?.fullName || "Member",
            value: cleanId(m?.user_id || m?.userId || m?._id || m?.id),
          })).filter((m: any) => m.value)
        : Array.from(assignedMemberIds).map((uid) => ({
            label: uid,
            value: uid,
          }));
      setTaskMemberOptions(memberOpts);

      setTaskForm({
        ...emptyTaskForm(),
        assignTo: teamOpts.map((t: any) => String(t.id)),
        assignToMember: memberOpts.map((m: any) => String(m.value)),
      });
    }
  };

  const handleTaskTeamsChange = async (values: Array<string | number>) => {
    const assignedMemberIds = new Set(parseLeadMemberIds(lead?.AssignToUser));
    const nextValues = values.map(String);
    const previousMembers = (taskForm.assignToMember || []).map(String);

    setTaskForm((prev) => ({
      ...prev,
      assignTo: nextValues,
    }));

    const preferredIds = new Set([
      ...Array.from(assignedMemberIds),
      ...previousMembers,
    ]);
    const preferred = await loadTaskMembersForTeams(
      nextValues,
      preferredIds,
      taskTeamOptions
    );

    // Keep previously selected members that are still available; else lead preferred
    const kept = previousMembers.filter((id) => preferred.includes(id));
    setTaskForm((prev) => ({
      ...prev,
      assignTo: nextValues,
      assignToMember: kept.length ? kept : preferred,
    }));
  };

  const handleTaskMembersChange = (values: Array<string | number>) => {
    setTaskForm((prev) => ({
      ...prev,
      assignToMember: values.map(String),
    }));
  };

  const handleSaveTask = async () => {
    if (!lead?._id) return;
    if (!taskForm.priority) {
      openErrorNotification("Priority is required");
      return;
    }
    setSavingTask(true);
    try {
      const memberIds = (taskForm.assignToMember || []).map(String);
      const payload = {
        leadId: lead._id,
        userId: JSON.stringify([
          store.getState().auth.userId,
          ...memberIds,
        ]),
        priority: taskForm.priority,
        assignTo: JSON.stringify(taskForm.assignTo),
        assignToMember: JSON.stringify(memberIds),
        dueDate: taskForm.dueDate,
        closedDate: taskForm.closedDate,
        description: taskForm.description,
      };
      const res = await addTask(payload);
      if (res?.success === 1 || res?.data) {
        openSuccessNotification(res?.message || "Task created successfully");
        closeTaskModal();
        const taskRes = await getTask(lead._id);
        setTasks(Array.isArray(taskRes?.data) ? taskRes.data : []);
      } else {
        openErrorNotification(res?.message || "Failed to create task");
      }
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to create task");
    } finally {
      setSavingTask(false);
    }
  };

  const noteColumns: YhTableColumn[] = [
    {
      field: "sno",
      headerName: "S.No",
      minWidth: 70,
      renderCell: ({ row }) => row.__index + 1,
    },
    { field: "notes", headerName: "Notes", flex: 1, minWidth: 220 },
    {
      field: "updatedBy",
      headerName: "Updated By",
      minWidth: 140,
      renderCell: ({ row }) => row.updatedBy || row.user_name || "—",
    },
    {
      field: "date",
      headerName: "Date Created",
      minWidth: 140,
      renderCell: ({ row }) => formatRecordedAt(row) || "—",
    },
  ];

  const reminderColumns: YhTableColumn[] = [
    {
      field: "sno",
      headerName: "S.No",
      minWidth: 70,
      renderCell: ({ row }) => row.__index + 1,
    },
    { field: "title", headerName: "Title", flex: 1, minWidth: 160 },
    { field: "notes", headerName: "Notes", flex: 1, minWidth: 180 },
    {
      field: "user_name",
      headerName: "Created By",
      minWidth: 140,
      renderCell: ({ row }) => row.user_name || row.updatedBy || "—",
    },
    {
      field: "date",
      headerName: "Date",
      minWidth: 140,
      renderCell: ({ row }) => {
        const date = String(row.date || "").trim();
        const time = String(row.time || "").trim();
        const clock = time.match(/^(\d{1,2}):(\d{2})/);
        if (/^\d{4}-\d{2}-\d{2}$/.test(date) && clock) {
          const local = parseLocalDateTime(
            `${date}T${clock[1].padStart(2, "0")}:${clock[2]}`
          );
          if (local) return formatDisplayDateTime(local);
        }
        return [date, time].filter(Boolean).join(" ") || "—";
      },
    },
  ];

  const taskColumns: YhTableColumn[] = [
    {
      field: "sno",
      headerName: "S.No",
      minWidth: 70,
      renderCell: ({ row }) => row.__index + 1,
    },
    {
      field: "description",
      headerName: "Task",
      flex: 1,
      minWidth: 180,
      renderCell: ({ row }) =>
        row.description || row.title || row.task || "—",
    },
    {
      field: "priority",
      headerName: "Priority",
      minWidth: 110,
      renderCell: ({ row }) => row.priority || row.status || "—",
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      minWidth: 130,
      renderCell: ({ row }) => row.dueDate || row.date || "—",
    },
    {
      field: "closedDate",
      headerName: "Close Date",
      minWidth: 130,
      renderCell: ({ row }) =>
        row.closedDate || row.closeDate || "—",
    },
    {
      field: "assignedFrom",
      headerName: "Assigned From",
      minWidth: 140,
      renderCell: ({ row }) =>
        row.assignedFrom || row.user_name || "—",
    },
  ];

  if (!lead?._id) {
    return (
      <div className="flex h-[calc(100vh-68px)] items-center justify-center bg-white">
        <div className="text-center">
          <p className="mb-4 text-sm text-[#6B7280]">Lead not found.</p>
          <YhButton variant="primary" onClick={handleBack}>
            Back to {isHometatvaLead ? "Hometatva Leads" : "All Leads"}
          </YhButton>
        </div>
      </div>
    );
  }

  const notesRows = notes.map((n, i) => ({ ...n, __index: i, id: n._id || i }));
  const reminderRows = reminders.map((n, i) => ({
    ...n,
    __index: i,
    id: n._id || i,
  }));
  const taskRows = tasks.map((n, i) => ({
    ...n,
    __index: i,
    id: n.task_id || n._id || i,
  }));

  return (
    <div className="relative flex h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] flex-col overflow-hidden bg-white p-4 pb-0 sm:p-5 sm:pb-0 lg:p-6 lg:pb-0">
      {/* Main two-column layout */}
      <div className="flex min-h-0 flex-1 gap-4 overflow-hidden pb-20">
        {/* Left sidebar */}
        <aside className="flex w-full max-w-[320px] shrink-0 flex-col overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.04)] lg:max-w-[340px]">
          <div className="bg-gradient-to-br from-[#841C39] via-[#A02448] to-[#A02448] px-5 pb-5 pt-6 text-white">
            <div className="flex items-center gap-3">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white/20 text-lg font-bold backdrop-blur">
                {initials}
              </div>
              <div className="min-w-0">
                <p className="truncate text-lg font-semibold">
                  {(isEditing ? editForm.leadName : lead.leadName) || "—"}
                </p>
                <p className="mt-0.5 inline-flex rounded-md bg-white/15 px-2 py-0.5 text-xs">
                  {lead.uid || "—"}
                </p>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleClickToCall}
                disabled={callBusy}
                className={`flex h-9 w-9 items-center justify-center rounded-lg border border-white/40 text-white transition ${
                  callBusy
                    ? "cursor-not-allowed opacity-50"
                    : "hover:bg-white/10"
                }`}
                aria-label={
                  hasActiveCall
                    ? `On call ${primary?.durationFormatted || ""}`
                    : "Call"
                }
                title={
                  hasActiveCall
                    ? `Ongoing IVR ${primary?.durationFormatted || ""}`
                    : "Start IVR call"
                }
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.81.36 1.6.7 2.35a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.75.34 1.54.57 2.35.7A2 2 0 0122 16.92z" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => handleWhatsApp()}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/40 text-white transition hover:bg-white/10"
                aria-label="WhatsApp"
                title="WhatsApp"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.5 3.5A11 11 0 004.1 17.8L3 21l3.3-1A11 11 0 0020.5 3.5zm-8.6 16a9 9 0 01-4.6-1.3l-.3-.2-2.7.7.7-2.6-.2-.3a9 9 0 1115.6-5.9 9 9 0 01-8.5 9.6zm5-6.7c-.3-.1-1.6-.8-1.8-.9s-.4-.1-.6.1-.7.9-.8 1-.3.2-.6.1a7.3 7.3 0 01-2.2-1.4 8 8 0 01-1.5-1.9c-.2-.3 0-.4.1-.6l.4-.5.1-.3a.5.5 0 000-.5l-.6-1.4c-.2-.4-.3-.4-.6-.4h-.5a1 1 0 00-.7.3 2.9 2.9 0 00-.9 2.1 5 5 0 001.1 2.6 11.5 11.5 0 004.4 3.9 5 5 0 002.5.8 2.4 2.4 0 001.6-.7 2 2 0 00.4-1.4c0-.2-.1-.3-.4-.4z" />
                </svg>
              </button>
              <button
                type="button"
                onClick={handleSms}
                className="flex h-9 items-center justify-center rounded-lg border border-white/40 px-2.5 text-xs font-semibold text-white transition hover:bg-white/10"
                aria-label="SMS"
                title="SMS"
              >
                SMS
              </button>
              <select
                aria-label="WhatsApp template"
                defaultValue=""
                onChange={(e) => {
                  const id = e.target.value;
                  if (!id) return;
                  handleWhatsApp(id);
                  e.target.value = "";
                }}
                className="h-9 max-w-[140px] rounded-lg border border-white/40 bg-transparent px-2 text-xs text-white"
              >
                <option value="" className="text-[#111827]">
                  Template…
                </option>
                {WHATSAPP_TEMPLATES.map((t) => (
                  <option key={t.id} value={t.id} className="text-[#111827]">
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto px-4 py-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
              Lead Form Details
            </p>
            <DetailRow
              label="Form Name"
              value={lead.form_name}
              icon={<span className="text-xs font-bold">F</span>}
            />
            <DetailRow
              label="Developer Name"
              value={lead.developer_name}
              icon={<span className="text-xs font-bold">D</span>}
            />
            <DetailRow
              label="Project Name"
              value={displayProjectName || lead.project_name}
              icon={<span className="text-xs font-bold">P</span>}
            />
            <DetailRow
              label="Project Type"
              value={lead.projecttype_name}
              icon={<span className="text-xs font-bold">T</span>}
            />
            <DetailRow
              label="Lead Source"
              value={lead.source}
              icon={<span className="text-xs font-bold">S</span>}
            />

            {extraFields.length > 0 ? (
              <>
                <div className="my-3 border-t border-[#EEF0F5]" />
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
                  Additional Fields
                </p>
                {extraFields.map((field) => (
                  <DetailRow
                    key={`${field.label}-${field.value}`}
                    label={field.label}
                    value={field.value}
                    icon={<span className="text-xs font-bold">+</span>}
                  />
                ))}
              </>
            ) : null}

            <div className="my-3 border-t border-[#EEF0F5]" />
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
              Contact Information
            </p>
            <DetailRow
              label="Full Name"
              value={isEditing ? editForm.leadName : lead.leadName}
              icon={<span className="text-xs font-bold">N</span>}
            />
            <DetailRow
              label="Email"
              value={isEditing ? editForm.leadEmail : lead.leadEmail}
              icon={<span className="text-xs font-bold">@</span>}
            />
            <DetailRow
              label="Phone Number"
              value={formatPhoneKeepingCountryCode(
                resolveLeadPhoneForDisplay(lead)
              )}
              icon={<span className="text-xs font-bold">#</span>}
            />
          </div>

          <div className="shrink-0 border-t border-[#EEF0F5] p-4">
            <YhButton
              variant="secondary"
              fullWidth
              onClick={startEdit}
            >
              Edit Lead Details
            </YhButton>
          </div>
        </aside>

        {/* Right content */}
        <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.04)]">
          <div className="flex shrink-0 gap-1 overflow-x-auto border-b border-[#EEF0F5] px-3 pt-2">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`relative whitespace-nowrap px-4 py-3 text-sm font-medium transition ${
                  tab === t.key
                    ? "text-[#841C39]"
                    : "text-[#6B7280] hover:text-[#111827]"
                }`}
              >
                {t.label}
                {tab === t.key ? (
                  <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-[#841C39]" />
                ) : null}
              </button>
            ))}
          </div>

          <div className="min-h-0 flex-1 overflow-auto p-4 sm:p-5">
            {tab === "activities" && (
              <div>
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-[#111827]">Activities</h2>
                  <p className="text-sm text-[#6B7280]">
                    Timeline of updates for this lead.
                  </p>
                </div>
                {historyRows.length === 0 ? (
                  <EmptyState
                    title="No activities found"
                    subtitle="There is no activity history for this lead yet."
                  />
                ) : (
                  <div className="space-y-0 border-l-2 border-[#F9F0F4] pl-5">
                    {historyRows.map((item: any) => {
                      const statusLabel =
                        item.new_value ||
                        item.title ||
                        item.action ||
                        item.stage ||
                        "Update";
                      const userName =
                        item.users?.[0]?.name ||
                        item.user_name ||
                        item.updatedByName ||
                        "";
                      const previousStage = String(item.old_value || "").trim();
                      const fromLabel =
                        previousStage &&
                        previousStage !== String(statusLabel)
                          ? `Changed from ${previousStage}`
                          : "";
                      const rawDate = item.updatedAt || item.createdAt || item.date;
                      let dateTimeLabel = "";
                      if (rawDate) {
                        const d = new Date(rawDate);
                        dateTimeLabel = Number.isNaN(d.getTime())
                          ? String(rawDate)
                          : d.toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                              hour12: false,
                            });
                      } else if (item.time) {
                        dateTimeLabel = String(item.time);
                      }

                      return (
                        <div key={item._id || item.log_id || item.id} className="relative pb-6">
                          <span className="absolute -left-[27px] top-1.5 h-3 w-3 rounded-full border-2 border-[#841C39] bg-white" />
                          <p className="text-sm font-semibold text-[#111827]">
                            {statusLabel}
                          </p>
                          {userName ? (
                            <p className="mt-0.5 text-xs text-[#6B7280]">
                              Lead status changed by {userName}
                            </p>
                          ) : null}
                          {fromLabel ? (
                            <p className="mt-0.5 text-xs text-[#9CA3AF]">
                              {fromLabel}
                            </p>
                          ) : null}
                          {dateTimeLabel ? (
                            <p className="mt-0.5 text-xs text-[#6B7280]">
                              {dateTimeLabel}
                            </p>
                          ) : null}
                          {item.notes || item.description ? (
                            <p className="mt-1 text-sm text-[#374151]">
                              {item.notes || item.description}
                            </p>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {tab === "details" && (
              <div>
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-[#111827]">Lead Details</h2>
                  <p className="text-sm text-[#6B7280]">
                    {isEditing
                      ? "Update contact details and save your changes."
                      : "Review lead profile and form information."}
                  </p>
                </div>
                <div className="rounded-2xl border border-[#EEF0F5] bg-[#F8F9FC] p-4 sm:p-5">
                  <p className="mb-3 text-sm font-semibold text-[#841C39]">
                    Lead Form Details
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <p className="mb-1 text-xs font-medium text-[#6B7280]">
                        Form Name
                      </p>
                      <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#111827]">
                        {lead.form_name || "—"}
                      </div>
                    </div>

                    {isEditing ? (
                      <YhSelect
                        label="Developer Name"
                        placeholder="Select developer"
                        searchable
                        searchPlaceholder="Search developers..."
                        showOptionIcon={false}
                        value={editForm.developerId}
                        options={developerOptions}
                        onChange={async (e) => {
                          const developerId = e.target.value;
                          setEditForm((prev) => ({
                            ...prev,
                            developerId,
                            projectId: "",
                          }));
                          try {
                            const projRes = developerId
                              ? await getProjectByDeveloper(developerId)
                              : await getProject();
                            const projRows = Array.isArray(projRes?.data)
                              ? projRes.data
                              : Array.isArray(projRes)
                                ? projRes
                                : [];
                            setProjectOptions(
                              projRows
                                .map((p: any) => ({
                                  label: p.project_name || p.name || "—",
                                  value: String(p._id || p.id || ""),
                                }))
                                .filter((o: any) => o.value)
                            );
                          } catch {
                            setProjectOptions([]);
                          }
                        }}
                      />
                    ) : (
                      <div>
                        <p className="mb-1 text-xs font-medium text-[#6B7280]">
                          Developer Name
                        </p>
                        <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#111827]">
                          {lead.developer_name || "—"}
                        </div>
                      </div>
                    )}

                    {isEditing ? (
                      <YhSelect
                        label="Project Name"
                        placeholder="Select project"
                        searchable
                        searchPlaceholder="Search projects..."
                        showOptionIcon={false}
                        value={editForm.projectId}
                        options={projectOptions}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            projectId: e.target.value,
                          }))
                        }
                      />
                    ) : (
                      <div>
                        <p className="mb-1 text-xs font-medium text-[#6B7280]">
                          Project Name
                        </p>
                        <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#111827]">
                          {displayProjectName || lead.project_name || "—"}
                        </div>
                      </div>
                    )}

                    {isEditing ? (
                      <YhSelect
                        label="Project Type"
                        placeholder="Select project type"
                        searchable
                        searchPlaceholder="Search types..."
                        showOptionIcon={false}
                        value={editForm.projecttypeId}
                        options={projectTypeOptions}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            projecttypeId: e.target.value,
                          }))
                        }
                      />
                    ) : (
                      <div>
                        <p className="mb-1 text-xs font-medium text-[#6B7280]">
                          Project Type
                        </p>
                        <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#111827]">
                          {lead.projecttype_name || "—"}
                        </div>
                      </div>
                    )}

                    <div>
                      <YhSelect
                        label="Stage"
                        placeholder="Select stage"
                        searchPlaceholder="Search stages..."
                        searchable
                        showOptionIcon={false}
                        value={lead.stage || ""}
                        groups={stageGroups}
                        onChange={(e) => {
                          if (e.target.value) {
                            handleStageChange(e.target.value);
                          }
                        }}
                      />
                    </div>

                    <div>
                      <p className="mb-1 text-xs font-medium text-[#6B7280]">
                        Source
                      </p>
                      {allowEditSource && sourceOptions.length > 0 ? (
                        <YhSelect
                          placeholder="Select source"
                          searchable
                          searchPlaceholder="Search sources..."
                          showOptionIcon={false}
                          value={lead.source || ""}
                          options={sourceOptions.map((s) => ({
                            label: s,
                            value: s,
                          }))}
                          onChange={(e) => {
                            if (e.target.value) {
                              updateLeadSource(e.target.value);
                            }
                          }}
                        />
                      ) : (
                        <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#111827]">
                          {lead.source || "—"}
                        </div>
                      )}
                    </div>

                    {isEditing ? (
                      <YhInput
                        label="Full Name"
                        value={editForm.leadName}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            leadName: e.target.value,
                          }))
                        }
                      />
                    ) : (
                      <div>
                        <p className="mb-1 text-xs font-medium text-[#6B7280]">
                          Full Name
                        </p>
                        <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#111827]">
                          {lead.leadName || "—"}
                        </div>
                      </div>
                    )}

                    {isEditing ? (
                      <YhInput
                        label="Email"
                        value={editForm.leadEmail}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            leadEmail: e.target.value,
                          }))
                        }
                      />
                    ) : (
                      <div>
                        <p className="mb-1 text-xs font-medium text-[#6B7280]">
                          Email
                        </p>
                        <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#111827]">
                          {lead.leadEmail || "—"}
                        </div>
                      </div>
                    )}

                    {isEditing && isOrgAdmin ? (
                      <YhInput
                        label="Phone"
                        value={editForm.leadPhone}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            leadPhone: e.target.value,
                          }))
                        }
                      />
                    ) : (
                      <div>
                        <p className="mb-1 text-xs font-medium text-[#6B7280]">
                          Phone
                          {!isOrgAdmin ? (
                            <span className="ml-1 font-normal text-[#9CA3AF]">
                              (masked)
                            </span>
                          ) : null}
                        </p>
                        <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#111827]">
                          {formatPhoneKeepingCountryCode(
                            resolveLeadPhoneForDisplay(lead)
                          ) || "—"}
                        </div>
                      </div>
                    )}
                  </div>

                  {extraFields.length > 0 ? (
                    <>
                      <p className="mb-3 mt-5 text-sm font-semibold text-[#841C39]">
                        Additional Fields
                      </p>
                      <div className="grid gap-4 sm:grid-cols-2">
                        {extraFields.map((field) => (
                          <div key={`extra-${field.label}-${field.value}`}>
                            <p className="mb-1 text-xs font-medium text-[#6B7280]">
                              {field.label}
                            </p>
                            <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#111827]">
                              {field.value || "—"}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            )}

            {tab === "calls" && (
              <EntityCallHistory
                phone={
                  lead?.leadPhoneFull ||
                  lead?.leadPhone ||
                  resolveLeadPhoneForDisplay(lead)
                }
                title="Call history"
                subtitle="All IVR and click-to-call attempts for this lead."
              />
            )}

            {tab === "task" && (
              <div className="flex h-full min-h-[360px] flex-col">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-[#111827]">Task</h2>
                    <p className="text-sm text-[#6B7280]">
                      View and manage all tasks for this lead.
                    </p>
                  </div>
                  <YhButton
                    variant="secondary"
                    size="sm"
                    onClick={openTaskModal}
                  >
                    + Add Task
                  </YhButton>
                </div>
                {taskRows.length === 0 ? (
                  <EmptyState
                    title="No tasks found"
                    subtitle="There are no tasks added for this lead yet."
                    actionLabel="+ Add Your First Task"
                    onAction={openTaskModal}
                  />
                ) : (
                  <YhTable
                    columns={taskColumns}
                    rows={taskRows}
                    getRowId={(r) => r.id || r._id}
                    rowHeight={56}
                    showPagination={false}
                    maxBodyHeight="calc(100vh - 320px)"
                  />
                )}
              </div>
            )}

            {tab === "reminder" && (
              <div className="flex h-full min-h-[360px] flex-col">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-[#111827]">Reminder</h2>
                    <p className="text-sm text-[#6B7280]">
                      View and manage all reminders for this lead.
                    </p>
                  </div>
                  <YhButton
                    variant="secondary"
                    size="sm"
                    onClick={() => setReminderModal(true)}
                  >
                    + Add Reminder
                  </YhButton>
                </div>
                {reminderRows.length === 0 ? (
                  <EmptyState
                    title="No reminders found"
                    subtitle="There are no reminders added for this lead yet."
                    actionLabel="+ Add Your First Reminder"
                    onAction={() => setReminderModal(true)}
                  />
                ) : (
                  <YhTable
                    columns={reminderColumns}
                    rows={reminderRows}
                    getRowId={(r) => r.id || r._id}
                    rowHeight={56}
                    showPagination={false}
                    maxBodyHeight="calc(100vh - 320px)"
                    loading={loading}
                  />
                )}
              </div>
            )}

            {tab === "notes" && (
              <div className="flex h-full min-h-[360px] flex-col">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-[#111827]">Notes</h2>
                    <p className="text-sm text-[#6B7280]">
                      View and manage all notes for this lead.
                    </p>
                  </div>
                  <YhButton
                    variant="secondary"
                    size="sm"
                    onClick={() => setNoteModal(true)}
                  >
                    + Add Note
                  </YhButton>
                </div>
                {notesRows.length === 0 ? (
                  <EmptyState
                    title="No notes found"
                    subtitle="There are no notes added for this lead yet."
                    actionLabel="+ Add Your First Note"
                    onAction={() => setNoteModal(true)}
                  />
                ) : (
                  <YhTable
                    columns={noteColumns}
                    rows={notesRows}
                    getRowId={(r) => r.id || r._id}
                    rowHeight={56}
                    showPagination={false}
                    maxBodyHeight="calc(100vh - 320px)"
                    loading={loading}
                  />
                )}
              </div>
            )}
          </div>
        </section>

        <AllLeadSideDrawer
          lead={lead}
          open={drawerOpen && !!lead?._id}
          onClose={() => setDrawerOpen(false)}
          onLeadChange={(next) => {
            setLead(next);
            try {
              sessionStorage.setItem(STORAGE_KEY(id), JSON.stringify(next));
            } catch {
              /* ignore */
            }
          }}
          onAssignSelectionChange={setDrawerAssign}
          onOpenTab={(t) => {
            setTab(t === "reminder" ? "reminder" : "notes");
          }}
        />
      </div>

      <YhModal
        open={noteModal}
        onClose={() => setNoteModal(false)}
        title="Create Note"
        footer={
          <div className="flex justify-end gap-2">
            <YhButton variant="secondary" onClick={() => setNoteModal(false)}>
              Cancel
            </YhButton>
            <YhButton variant="primary" onClick={handleSaveNote}>
              Save Note
            </YhButton>
          </div>
        }
      >
        <p className="mb-2 text-sm text-[#6B7280]">
          Notes help you remember important details about your leads.
        </p>
        <textarea
          className="min-h-[120px] w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:border-[#A02448]"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Write a note..."
        />
      </YhModal>

      <YhModal
        open={reminderModal}
        onClose={() => setReminderModal(false)}
        title="Create Reminder"
        footer={
          <div className="flex justify-end gap-2">
            <YhButton variant="secondary" onClick={() => setReminderModal(false)}>
              Cancel
            </YhButton>
            <YhButton variant="primary" onClick={handleSaveReminder}>
              Save Reminder
            </YhButton>
          </div>
        }
      >
        <div className="space-y-3">
          <YhInput
            label="Reminder Title"
            placeholder="Enter reminder title"
            value={reminderForm.title}
            onChange={(e) =>
              setReminderForm((p) => ({ ...p, title: e.target.value }))
            }
          />
          <YhDateTimePicker
            label="Date and Time"
            value={reminderForm.dateTime}
            onChange={(val) =>
              setReminderForm((p) => ({ ...p, dateTime: val }))
            }
          />
          <textarea
            className="min-h-[100px] w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:border-[#A02448]"
            value={reminderForm.notes}
            onChange={(e) =>
              setReminderForm((p) => ({ ...p, notes: e.target.value }))
            }
            placeholder="Notes"
          />
        </div>
      </YhModal>

      <YhModal
        open={taskModal}
        onClose={closeTaskModal}
        title="Create Task"
        width={720}
        footer={
          <div className="flex justify-end gap-2">
            <YhButton
              variant="secondary"
              onClick={closeTaskModal}
              disabled={savingTask}
            >
              Cancel
            </YhButton>
            <YhButton
              variant="primary"
              onClick={handleSaveTask}
              disabled={savingTask}
            >
              {savingTask ? "Saving..." : "Save Task"}
            </YhButton>
          </div>
        }
      >
        <div className="space-y-4">
          <YhSelect
            label="Priority"
            placeholder="Select"
            searchable={false}
            showOptionIcon={false}
            value={taskForm.priority}
            options={PRIORITY_OPTIONS}
            onChange={(e) =>
              setTaskForm((p) => ({ ...p, priority: e.target.value }))
            }
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <YhMultiSelect
              label="Assign To"
              placeholder="Select teams..."
              searchable
              showOptionIcon={false}
              maxVisibleChips={2}
              options={taskTeamOptions.map((t) => ({
                label: t.label,
                value: String(t.id),
              }))}
              value={(taskForm.assignTo || []).map(String)}
              onChange={handleTaskTeamsChange}
            />
            <YhMultiSelect
              label="Assign To Member"
              placeholder="Select members..."
              searchable
              showOptionIcon={false}
              maxVisibleChips={2}
              options={taskMemberOptions.map((m) => ({
                label: m.label,
                value: String(m.value),
              }))}
              value={(taskForm.assignToMember || []).map(String)}
              onChange={handleTaskMembersChange}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <YhDatePicker
              label="Task Due Date"
              value={taskForm.dueDate}
              onChange={(val) =>
                setTaskForm((p) => ({ ...p, dueDate: val }))
              }
            />
            <YhDatePicker
              label="Task Close Date"
              value={taskForm.closedDate}
              onChange={(val) =>
                setTaskForm((p) => ({ ...p, closedDate: val }))
              }
            />
          </div>

          <YhInput
            label="Description"
            multiline
            rows={4}
            placeholder="Hierarchy Description"
            value={taskForm.description}
            onChange={(e) =>
              setTaskForm((p) => ({ ...p, description: e.target.value }))
            }
          />
        </div>
      </YhModal>

      {/* Sticky footer */}
      <div className="sticky bottom-0 z-20 -mx-4 mt-auto flex shrink-0 items-center justify-between gap-3 border-t border-[#EEF0F5] bg-white/95 px-4 py-3 backdrop-blur sm:-mx-5 sm:px-5 lg:-mx-6 lg:px-6">
        <button
          type="button"
          onClick={handleBack}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white text-[#374151] shadow-sm transition hover:bg-[#F3F4F6]"
          aria-label="Back"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        {tab === "details" ? (
          isEditing ? (
            <div className="flex items-center gap-2">
              <YhButton
                variant="secondary"
                size="sm"
                fullWidth={false}
                className="!w-auto"
                onClick={cancelEdit}
                disabled={saving}
              >
                Cancel
              </YhButton>
              <YhButton
                variant="primary"
                size="sm"
                fullWidth={false}
                className="!w-auto"
                onClick={handleSaveLead}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </YhButton>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {!drawerOpen ? (
                <YhButton
                  variant="secondary"
                  size="sm"
                  fullWidth={false}
                  className="!w-auto"
                  onClick={() => setDrawerOpen(true)}
                >
                  Open Panel
                </YhButton>
              ) : null}
              <YhButton
                variant="primary"
                size="sm"
                fullWidth={false}
                className="!w-auto"
                onClick={startEdit}
              >
                Edit
              </YhButton>
            </div>
          )
        ) : !drawerOpen ? (
          <YhButton
            variant="secondary"
            size="sm"
            fullWidth={false}
            className="!w-auto"
            onClick={() => setDrawerOpen(true)}
          >
            Open Panel
          </YhButton>
        ) : (
          <div />
        )}
      </div>

      <YhModal
        open={customerConfirm.open}
        onClose={() =>
          !convertingCustomer && setCustomerConfirm({ open: false, stage: "" })
        }
        title="Convert to Customer"
        width={420}
        footer={
          <div className="flex justify-end gap-2">
            <YhButton
              variant="secondary"
              size="sm"
              disabled={convertingCustomer}
              onClick={() => setCustomerConfirm({ open: false, stage: "" })}
            >
              No
            </YhButton>
            <YhButton
              variant="primary"
              size="sm"
              disabled={convertingCustomer}
              onClick={confirmConvertCustomer}
            >
              {convertingCustomer ? "Converting..." : "Yes"}
            </YhButton>
          </div>
        }
      >
        <p className="text-sm text-[#374151]">
          Are you sure to make customer?
        </p>
      </YhModal>

      <StageReasonModal
        open={stageReason.open}
        stageLabel={stageReason.stage}
        saving={savingStageReason}
        onCancel={() => setStageReason({ open: false, stage: "" })}
        onConfirm={confirmStageReason}
      />
    </div>
  );
};

export default AllLeadsDetails;
